import { z } from 'zod';

import { idSchema, isoDateTimeSchema, slugSchema } from './common.js';
import { uploadSchema } from './upload.js';

/**
 * Tipos de documento da página Transparência. Espelha o enum no banco.
 */
export const documentTypeSchema = z.enum([
  'politica-integridade',
  'codigo-conduta',
  'politica-privacidade',
  'termos-uso',
  'relatorio',
  'release-imprensa',
  'outro',
]);
export type DocumentType = z.infer<typeof documentTypeSchema>;

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  'politica-integridade': 'Política de Integridade',
  'codigo-conduta': 'Código de Conduta',
  'politica-privacidade': 'Política de Privacidade (LGPD)',
  'termos-uso': 'Termos de Uso',
  relatorio: 'Relatório',
  'release-imprensa': 'Release de Imprensa',
  outro: 'Outro',
};

/**
 * Documento institucional. `arquivo` é o Upload populado pelo backend
 * (inclui URL pronta). `arquivoId` é o FK no banco; o client passa apenas
 * o id no create/update.
 */
export const documentSchema = z.object({
  slug: slugSchema,
  titulo: z.string().min(1).max(200),
  descricao: z.string().nullable(),
  arquivoId: idSchema,
  arquivo: uploadSchema,
  tipo: documentTypeSchema,
  versao: z.string().max(40).nullable(),
  revisadoEm: isoDateTimeSchema.nullable(),
  /** Ordem editorial na página pública. Menor aparece primeiro. */
  ordem: z.number().int(),
  visivel: z.boolean(),
  criadoEm: isoDateTimeSchema,
  atualizadoEm: isoDateTimeSchema,
});
export type Document = z.infer<typeof documentSchema>;

export const createDocumentInputSchema = z.object({
  slug: slugSchema,
  titulo: z.string().min(1).max(200),
  descricao: z.string().max(2000).optional().or(z.literal('')),
  arquivoId: idSchema,
  tipo: documentTypeSchema,
  versao: z.string().max(40).optional().or(z.literal('')),
  /** ISO date (YYYY-MM-DD) ou full datetime. Convertido para Date no backend. */
  revisadoEm: z.string().optional().or(z.literal('')),
  ordem: z.number().int().min(0).max(9999),
  visivel: z.boolean(),
});
export type CreateDocumentInput = z.infer<typeof createDocumentInputSchema>;

export const updateDocumentInputSchema = createDocumentInputSchema.partial().omit({ slug: true });
export type UpdateDocumentInput = z.infer<typeof updateDocumentInputSchema>;

export const listDocumentsQuerySchema = z.object({
  tipo: documentTypeSchema.optional(),
  visivel: z.preprocess(
    (v) => (v === 'true' ? true : v === 'false' ? false : undefined),
    z.boolean().optional(),
  ),
});
export type ListDocumentsQuery = z.infer<typeof listDocumentsQuerySchema>;
